import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { PayrollService } from '../../../../services/payroll.service';
import {
  PayDateInfo,
  PayDateInfoEx,
  TimeclockDetail,
  TimeclockFile,
  TimeclockModel,
  TimeclockValidation,
  TimeclockValidationResult
} from '../../../../models/payroll';
import { Store } from '@ngxs/store';
import { ClientSelectionState } from '../../../../state/ClientSelectionState/ClientSelection.state';
import { GridCheckboxComponent } from '../../../../components/grid/grid-checkbox/grid-checkbox.component';
import { CellEditorComponent } from '../../../../components/grid/cell-editor/cell-editor.component';
import { GridApi, GridOptions, TextCellEditor } from 'ag-grid-community';
import { CompaniesService } from '../../../../services/companies.service';
import { forkJoin } from 'rxjs';
import { Division } from '../../../../models/division';
import { ClientSetup } from '../../../../models/client';
import { formatDatePickerValue } from '../../../../utils/datepicker-value-parser';
import { SelectEditorComponent } from '../../../../components/grid/select-editor/select-editor.component';
import { CellRendererComponent } from '../../../../components/grid/cell-renderer/cell-renderer.component';
import { SelectRendererComponent } from '../../../../components/grid/select-renderer/select-renderer.component';
import { SharedService } from '../../../../services/shared.service';
import { Router } from '@angular/router';
import { CustomGridIconComponent } from '../../../../components/grid/custom-grid-icon/custom-grid-icon.component';
import { TimeclockCustomIconComponent } from './timeclock-custom-icon/timeclock-custom-icon.component';
import { JobCode } from '../../../../models/labor-allocation';
import { KeyPair, KeyTriple } from '../../../../models/base';
import { CompanyEarningConfig } from '../../../../models/company-earning-config';

@Component({
  selector: 'lib-timeclock-editor',
  standalone: false,
  templateUrl: './timeclock-editor.component.html',
  styleUrl: './timeclock-editor.component.scss'
})
export class TimeclockEditorComponent implements OnInit {
  @Input()
  public selCalendarId = null;
  fileToEdit: TimeclockFile;
  rowData: TimeclockDetailRow[] = [];
  allRowData: TimeclockDetailRow[] = [];

  edited = false;
  errors = false;

  @Output()
  goBack = new EventEmitter<void>();

  public gridHeight = '600px';
  @Input()
  header: { file: TimeclockModel; pdi: PayDateInfoEx };

  public periodEndDate: any;
  public colDefs: any[] = [];
  public gridApi: GridApi<any>;
  public divisions: Division[] | null = null;
  public jobCodes: KeyTriple[] | null = null;
  public earnings: CompanyEarningConfig[] | null = null;
  private clientSetup: ClientSetup;
  private clientCode: string;

  public rowFilterIndicator: string;

  constructor(
    private payrollService: PayrollService,
    private store: Store,
    private companiesService: CompaniesService,
    private sharedService: SharedService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.store.selectOnce(ClientSelectionState.clientCode).subscribe(code => {
      this.clientCode = code;
      const observables = [
        this.payrollService.getTimeclockDetails(
          code,
          this.header.file.Id,
          this.header.file.PersistedToAgile
        ),
        this.companiesService.getDivisions(code),
        this.store.selectOnce(ClientSelectionState.clientSetup),
        this.companiesService.getJobCodes(code),
        this.companiesService.getEarnings(code)
      ];
      forkJoin(observables).subscribe((response: any[]) => {
        this.fileToEdit = response[0];
        this.allRowData = this.fileToEdit.Details.map(detail =>
          new TimeclockDetailRow().fromDetail(detail, response[1])
        );
        this.onRowViewChange('all');
        this.divisions = response[1];
        this.clientSetup = response[2];
        this.jobCodes = response[3];
        this.earnings = response[4];
        this.periodEndDate = formatDatePickerValue(this.header.pdi.PeriodEnd);
        this._initGrid();
      });
    });
  }

  approveFile() {
    this.edited = false;
    this.errors = false;
    this.fileToEdit.Details = this.allRowData;
    this.payrollService
      .validateTimeclockFile(this.clientCode, this.fileToEdit)
      .subscribe(result => {
        this.rowData.forEach(row => {
          const rowResult = result.find(x => x.Record.DetailId == row.DetailId);
          if (rowResult) {
            this._getErrors(rowResult);
          }
        });
        if (!this.errors) {
          //All valid, approve the file
          this.payrollService
            .approveTimeclockFile(this.clientCode, this.fileToEdit)
            .subscribe(file => {
              this.sharedService.timeclockSelection = {
                TimeClockFile: file
              };
              this._goToPayrollStart();
            });
        }
      });
  }

  onGridReady(params) {
    this.gridApi = params.api;
  }

  onCellEditingStopped(params) {
    this.edited = true;
  }

  public navigateToPayrollHub() {
    this.router.navigate([`/client/${this.clientCode}/payroll-landing`]);
  }

  private _initGrid() {
    this._autoSizeGridHeight();
    const colDefs: any[] = [
      {
        field: 'Selected',
        headerName: '',
        cellRenderer: GridCheckboxComponent,
        sortable: false,
        pinned: 'left',
        maxWidth: 100
        //  cellStyle: params => this._setCellStyle(params)
      },
      {
        field: 'ErrorsAndWarningsSort',
        headerName: '',
        cellRenderer: TimeclockCustomIconComponent,
        type: ['immutable'],
        sortable: false,
        pinned: 'left',
        maxWidth: 75,
        tooltipField: 'Message'
      },
      {
        field: 'EmployeeId',
        cellDataType: 'text',
        headerName: 'EE',
        pinned: 'left',
        editable: true,
        maxWidth: 100,
        cellEditor: CellEditorComponent,
        cellEditorParams: { mode: 'number', decimals: 0 },
        cellStyle: params => this._setCellStyle(params)
      },
      {
        field: 'EmployeeName',
        cellDataType: 'text',
        headerName: 'Name',
        pinned: 'left',
        editable: true,
        cellEditor: TextCellEditor,
        cellStyle: params => this._setCellStyle(params)
      },
      {
        field: 'EarningCode',
        cellDataType: 'text',
        headerName: 'Earning',
        pinned: 'left',
        editable: true,
        maxWidth: 150,
        cellEditor: SelectEditorComponent,
        cellEditorParams: {
          values: this.earnings.map(x => {
            return {
              value: x.Name,
              label: x.Name
            };
          })
        },
        cellRenderer: SelectRendererComponent,
        cellRendererParams: {
          values: this.earnings.map(x => {
            return {
              value: x.Name,
              label: x.Name
            };
          })
        },
        cellStyle: params => this._setCellStyle(params)
      },
      {
        field: 'Rate',
        cellDataType: 'text',
        headerName: 'Rate',
        editable: true,
        maxWidth: 200,
        cellEditor: CellEditorComponent,
        cellRenderer: CellRendererComponent,
        cellEditorParams: { mode: 'currency', decimals: 2 },
        cellRendererParams: params => {
          return {
            isPlaceholder: true,
            placeholder: params.data.Rate,
            mode: 'currency'
          };
        },
        cellStyle: params => this._setCellStyle(params)
      },
      {
        field: 'Hours',
        cellDataType: 'text',
        headerName: 'Hours',
        editable: true,
        maxWidth: 200,
        cellEditor: CellEditorComponent,
        cellRenderer: CellRendererComponent,
        cellEditorParams: { mode: 'number', decimals: 2 },
        cellRendererParams: params => {
          return {
            isPlaceholder: true,
            placeholder: params.data.Hours,
            mode: 'number'
          };
        },
        cellStyle: params => this._setCellStyle(params)
      },
      {
        field: 'Amount',
        cellDataType: 'text',
        headerName: 'Amount',
        editable: true,
        maxWidth: 200,
        cellEditor: CellEditorComponent,
        cellRenderer: CellRendererComponent,
        cellEditorParams: { mode: 'currency', decimals: 2 },
        cellRendererParams: params => {
          return {
            isPlaceholder: true,
            placeholder: params.data.Amount,
            mode: 'currency'
          };
        },
        cellStyle: params => this._setCellStyle(params)
      }
    ];
    if (this.divisions && this.divisions.length > 0) {
      colDefs.push({
        field: 'DivisionNumber',
        cellDataType: 'text',
        headerName: 'Division',
        editable: true,
        minWidth: 150,
        cellEditor: SelectEditorComponent,
        cellEditorParams: {
          values: this.divisions.map(x => {
            return {
              value: x.Id,
              label: `${x.DisplayId} - ${x.Name}`
            };
          })
        },
        cellRenderer: SelectRendererComponent,
        cellRendererParams: {
          values: this.divisions.map(x => {
            return {
              value: x.Id,
              label: `${x.DisplayId} - ${x.Name}`
            };
          })
        },
        cellStyle: params => this._setCellStyle(params)
      });
      if (this.divisions.some(y => y.Departments.length > 0)) {
        colDefs.push({
          field: 'DepartmentNumber',
          cellDataType: 'text',
          headerName: 'Dept',
          editable: true,
          minWidth: 150,
          cellEditor: SelectEditorComponent,
          cellEditorParams: params => {
            const division = this.divisions.find(x => x.Id == params.data.DivisionNumber);
            if (division) {
              return {
                values: division.Departments.map(x => {
                  return {
                    value: x.Id,
                    label: `${x.DisplayId} - ${x.Value}`
                  };
                })
              };
            }
            return {
              values: []
            };
          },
          cellRenderer: SelectRendererComponent,
          cellRendererParams: params => {
            const division = this.divisions.find(x => x.Id == params.data.DivisionNumber);
            if (division) {
              return {
                values: division.Departments.map(x => {
                  return {
                    value: x.Id,
                    label: `${x.DisplayId} - ${x.Value}`
                  };
                })
              };
            }
            return {
              values: []
            };
          },
          cellStyle: params => this._setCellStyle(params)
        });
      }
    }
    if (this.clientSetup.HasJobCodes) {
      colDefs.push({
        field: 'JobCode',
        cellDataType: 'text',
        headerName: 'Job Code',
        editable: true,
        minWidth: 150,
        cellEditor: SelectEditorComponent,
        cellEditorParams: {
          values: this.jobCodes.map(x => {
            return {
              value: x.Id,
              label: `${x.DisplayId} - ${x.Value}`
            };
          })
        },
        cellRenderer: SelectRendererComponent,
        cellRendererParams: {
          values: this.jobCodes.map(x => {
            return {
              value: x.Id,
              label: `${x.Value}`
            };
          })
        },
        cellStyle: params => this._setCellStyle(params)
      });
    }
    this.colDefs = colDefs;
    this.edited = false;
    this.errors = false;
    this.fileToEdit.Details = this.allRowData;
    this.payrollService
      .validateTimeclockFile(this.clientCode, this.fileToEdit)
      .subscribe(result => {
        this.rowData.forEach(row => {
          const rowResult = result.find(x => x.Record.DetailId == row.DetailId);
          if (rowResult) {
            this._getErrors(rowResult);
          }
        });
      });
  }

  private _autoSizeGridHeight() {
    // NOTE: this is a workaround to set the grid height to fit the number of rows
    // grid header rows (with top group row) ~100px
    // each data row is ~45px
    // 20px for horizonal scroll bar
    // max height currently set to 600px
    const header = 30;
    const rows = this.rowData.length * 45;
    const scrollbar = 20;
    const noRows = this.rowData.length == 0 ? 80 : 0;
    this.gridHeight = header + rows + scrollbar + noRows + 'px';
  }

  public runPayroll(useFile: boolean = false) {
    this.sharedService.timeclockSelection = {};
    this._goToPayrollStart();
  }
  private _goToPayrollStart(params?: {}) {
    // if calendar_id query param exists, append it back to the query params
    // to the payroll start page
    if (this.selCalendarId) {
      params = { ...params, ...{ calendar_id: this.selCalendarId } };
    }

    this.router.navigate([`/client/${this.clientCode}/payroll`], { queryParams: params });
  }

  private _getErrors(validationResult: TimeclockValidation) {
    let message = '';
    let warn = false;
    let error = false;
    //Warning states
    if (validationResult.ValidationResult.FirstLastNameMatch) {
      warn = true;
      message = `${message} Employee first/last name does not match.\n`;
    }
    if (validationResult.ValidationResult.DivisionRequired) {
      warn = true;
      message = `${message} Division required.\n`;
    }
    if (validationResult.ValidationResult.DivisionNotFound) {
      warn = true;
      message = `${message} Division not found on the client.\n`;
    }
    if (validationResult.ValidationResult.JobCodeMissing) {
      warn = true;
      message = `${message} Job code not entered...the job code of 'None' will be used.\n`;
    }
    if (validationResult.ValidationResult.NoDivisionMatch) {
      warn = true;
      message = `${message}  Employee division does not match.\n`;
    }
    if (validationResult.ValidationResult.NoDepartmentMatch) {
      warn = true;
      message = `${message} Employee department does not match.\n`;
    }
    if (validationResult.ValidationResult.NoSSNMatch) {
      warn = true;
      message = `${message} Employee SS number does not match.\n`;
    }

    //Error states
    if (validationResult.ValidationResult.EmployeeNumberMissing) {
      error = true;
      message = `${message} Employee number is missing.\n`;
    }
    if (validationResult.ValidationResult.EmployeeIdMissing) {
      error = true;
      message = `${message} Employee id not on client.\n`;
    }
    if (validationResult.ValidationResult.EarningNotActive) {
      error = true;
      message = `${message} Earning is not active on employee.\n`;
    }
    if (validationResult.ValidationResult.EarningNotOnClient) {
      error = true;
      message = `${message} Earning not on client.\n`;
    }
    if (validationResult.ValidationResult.EarningNameMissing) {
      error = true;
      message = `${message} Earning name is missing.\n`;
    }
    if (validationResult.ValidationResult.EarningNotOnEmployee) {
      error = true;
      message = `${message} Earning not on employee.\n`;
    }
    if (validationResult.ValidationResult.DepartmentRequired) {
      error = true;
      message = `${message} Department required.\n`;
    }
    if (validationResult.ValidationResult.DepartmentNotOnClient) {
      error = true;
      message = `${message} Must enter a valid Department in the file, or leave it blank to use the Payee's default Department.\n`;
    }
    if (validationResult.ValidationResult.JobCodeNotOnClient) {
      error = true;
      message = `${message} Job code not found on the client.\n`;
    }
    const row = this.rowData.find(x => x.DetailId == validationResult.Record.DetailId);
    row.Error = error;
    if (row.Error) {
      this.errors = true;
    }
    row.Warning = warn;
    row.Message = message;
    row.ValidationResult = validationResult.ValidationResult;
    this.gridApi?.redrawRows();
  }

  private _shouldSetErrorStyle(field: string, validation: TimeclockValidationResult): WarningType {
    if (validation == null) {
      return WarningType.None;
    }
    switch (field) {
      case 'EmployeeNumber':
        if (validation.EmployeeNumberMissing || validation.EmployeeIdMissing) {
          return WarningType.Error;
        }
        return WarningType.None;
      case 'EmployeeName':
        if (validation.FirstLastNameMatch) {
          return WarningType.Warning;
        }
        return WarningType.None;
      case 'EarningCode':
        if (
          validation.EarningNotActive ||
          validation.EarningNotOnClient ||
          validation.EarningNameMissing ||
          validation.EarningNotOnEmployee
        ) {
          return WarningType.Error;
        }
        return WarningType.None;
      case 'Rate':
        return WarningType.None;
      case 'Hours':
        return WarningType.None;
      case 'Amount':
        return WarningType.None;
      case 'DivisionNumber':
        if (
          validation.DivisionRequired ||
          validation.DivisionNotFound ||
          validation.NoDivisionMatch
        ) {
          return WarningType.Warning;
        }
        return WarningType.None;
      case 'DepartmentNumber':
        if (validation.DepartmentRequired || validation.DepartmentNotOnClient) {
          return WarningType.Error;
        } else if (validation.NoDivisionMatch) {
          return WarningType.Warning;
        }
        return WarningType.None;
      case 'JobCode':
        if (validation.JobCodeNotOnClient) {
          return WarningType.Error;
        } else if (validation.JobCodeMissing) {
          return WarningType.Warning;
        }
        return WarningType.None;
      default:
        return WarningType.None;
    }
  }

  private _setCellStyle(params, defaultStyle = {}) {
    const warningType = this._shouldSetErrorStyle(
      params.colDef.field,
      params.data.ValidationResult
    );
    if (warningType == WarningType.Error) {
      return {
        ...defaultStyle,
        border: '2px solid rgba(251, 29, 29, 1)',
        'border-radius': '3px'
      };
    } else if (warningType == WarningType.Warning) {
      return {
        ...defaultStyle,
        border: '2px solid rgba(255, 191, 1, 1)',
        'border-radius': '3px'
      };
    } else {
      return { ...defaultStyle };
    }
  }
  public onRowViewChange(view: string) {
    let rowData = [...this.allRowData];
    let data = [];
    // indicator says something like 'Displaying 1 error row of 100 total rows'
    this.rowFilterIndicator = 'Displaying';
    switch (view) {
      case 'all': {
        this.rowData = rowData;
        break;
      }
      case 'warnings': {
        rowData.forEach(x => {
          if (x.Warning || x.Info) {
            data.push(x);
          }
        });
        this.rowData = data;
        this.rowFilterIndicator += ` ${this.rowData.length} warning ${this.pluralize(
          'row',
          this.rowData.length,
          's'
        )} of`;
        break;
      }
      case 'errors': {
        rowData.forEach(x => {
          if (x.Error) {
            data.push(x);
          }
        });
        this.rowData = data;
        this.rowFilterIndicator += ` ${this.rowData.length} error ${this.pluralize(
          'row',
          this.rowData.length,
          's'
        )} of`;
        break;
      }
    }

    this.rowFilterIndicator += ` ${this.allRowData.length} total ${this.pluralize(
      'row',
      this.allRowData.length,
      's'
    )}`;
  }
  public pluralize(value: string, length, suffix: string, irregular: boolean = false) {
    let suffixed = irregular ? suffix : `${value}${suffix}`;
    return length === 1 ? value : suffixed;
  }

  protected readonly formatDatePickerValue = formatDatePickerValue;
}

class TimeclockDetailRow implements TimeclockDetail {
  Selected: boolean;
  Error: boolean;
  Warning: boolean;
  Info: boolean;
  Message: string; // for tooltip hover
  // for column sorting purposes. 0: nothing, 1: error, 2: warning, 3: informational
  ErrorsAndWarningsSort: 0;
  DetailId?: number;
  EmployeeId?: number;
  EmployeeNumber?: number;
  EmployeeName: string;
  EarningCode: string;
  Rate?: number;
  Hours?: number;
  Amount?: number;
  DivisionNumber?: number;
  DepartmentNumber?: number;
  JobCode: string;
  LocationId?: number;

  ValidationResult: TimeclockValidationResult | null;

  fromDetail(detail: TimeclockDetail, divisions: Division[]): TimeclockDetailRow {
    this.Selected = true;
    this.DetailId = detail.DetailId;
    this.EmployeeId = detail.EmployeeId;
    this.EmployeeNumber = detail.PersonId;
    this.EmployeeName = detail.EmployeeName;
    this.EarningCode = detail.EarningCode;
    this.Rate = detail.Rate;
    this.Hours = detail.Hours;
    this.Amount = detail.Amount;
    //Ignore division number, and populate based on department
    const div = divisions.find(x => x.Departments.some(y => y.Id == detail.DepartmentNumber));
    this.DivisionNumber = div?.Id;
    this.DepartmentNumber = detail.DepartmentNumber;
    this.JobCode = detail.JobCode;
    this.LocationId = detail.LocationId;
    return this;
  }
}

enum WarningType {
  None,
  Warning,
  Error
}
